import getGoogleRecommendedArtworks from 'apis/google'
import { getDataLayerItemVariant } from 'lib/helpers'
import {
  convertFromCentsToDollars,
  convertFromCentsToDollarsString,
} from 'components/FormatPrice/helpers'
import { algoliaEventsApi } from 'apis/algolia'
import { getAlgoliaQueryIDFromLocalStorage } from 'lib/algolia/v2/helpers'
import { AddToCartGAWhitelistParams, ArtworkObject, GA4CartEventObject } from './types'
import { parseSku } from 'lib/product'
import { fetchRelatedArtworks } from './apis/carousel'
import {
  ConstructorResultsSdkResponseObject,
  ConstructorResultsData,
} from 'components/SA/SearchProvider/constructorTypes'
import { getCategoryFromGroups } from 'components/SA/SearchProvider/libs/helpers/constructor'

const { ALGOLIA_APPLICATION_ID, ALGOLIA_ENV, ALGOLIA_SEARCH_API, APP_ENV } = process.env
const applicationId: string = ALGOLIA_APPLICATION_ID || ''
const searchApi: string = ALGOLIA_SEARCH_API || ''
const algoliaEnv: string = ALGOLIA_ENV || APP_ENV || 'production'
const searchIndice: string = `${algoliaEnv}_all_artworks`

export const config: Record<string, any> = {
  applicationId,
  searchApi,
  searchIndice,
}

const PRODUCT_TYPES = {
  [`1`]: 'original',
  [`2`]: 'open',
  [`3`]: 'open',
  [`4`]: 'original',
}

// Mapping to filter by category the recommendations
export const CATEGORY_CONSTRUCTOR_MAPPING = {
  painting: 'paintings',
  drawing: 'drawings',
}

// Mapping to pluralize categories
export const CATEGORY_PLURALIZE_MAPPING = {
  'mixed-media': 'Mixed Media Artworks',
  digital: 'Digital Artworks',
  photography: 'Photographs',
  printmaking: 'Prints',
}

// Carousel Api Type
export const ALGOLIA = 'algolia'
export const VISII = 'visii'
export const GOOGLE = 'google'
export const CONSTRUCTOR = 'constructor'

// Recommendation API expects the following ATTRIBUTES
const PDP_PARAMS = {
  event: 'detail-page-view',
  pageSize: 25,
  placement: 'pdp-bottom',
  experiment: '',
}

const PLP_PARAMS = {
  event: 'category-page-view',
  pageSize: 28,
  placement: 'recently-viewed',
  experiment: '',
}

export const getPluralizedCategory = (value: string): string => {
  return CATEGORY_PLURALIZE_MAPPING[value] || `${value}s`
}

export const getLinkCategory = (value: string): string => {
  let category

  switch (value) {
    case 'Drawing':
      category = 'Drawings'
      break

    case 'Painting':
      category = 'Paintings'
      break

    default:
      category = value
      break
  }

  return category
}

export const getRelatedArtworks = async ({
  artworkId,
  callBack,
  category,
  destinationZone,
  isArtist,
  mediums,
  metadata,
  sku,
  styles,
  subject,
}: Record<string, any>): Promise<void> => {
  // Google Expects Categories to have no trailing `s` Ex: Paintings => Painting
  const artworks = await fetchRelatedArtworks({
    artworkId,
    category,
    destinationZone,
    isArtist,
    mediums,
    metadata,
    sku,
    styles,
    subject,
  })
  callBack(artworks)
}

export const getGoogleArtworksPDP = async ({
  artworkId,
  category,
  callBack,
  visitorId,
  filter,
}: Record<string, any>): Promise<void> => {
  // Google Expects Categories to have no trailing `s` Ex: Paintings => Painting
  const { payload: artworks, success } = await getGoogleRecommendedArtworks({
    ...PDP_PARAMS,
    artworkId,
    category,
    visitorId,
    filter,
  })

  if (success) {
    callBack(artworks)
  }
}

export const getGoogleArtworksPLP = async ({
  category,
  callBack,
  visitorId,
  pageCategories,
}: Record<string, any>): Promise<void> => {
  // Google Expects Categories to have no trailing `s` Ex: Paintings => Painting
  const { payload: artworks, success } = await getGoogleRecommendedArtworks({
    ...PLP_PARAMS,
    category,
    visitorId,
    pageCategories,
  })

  if (success) {
    callBack(artworks)
  }
}

export const clickArtworkCarousel = (
  ev: React.SyntheticEvent<Record<string, any>>,
  algoliaSearchIndex?: string,
  algoliaUserToken?: string,
  userID?: number
) => {
  const algoliaQueryIdFromLocalStorage = getAlgoliaQueryIDFromLocalStorage()
  const {
    id,
    position,
    list,
    name,
    category,
    listid,
    listname,
    artid,
    brand,
    price,
  } = ev.currentTarget.dataset
  // Delete this dataLayer event in July as part of universal analytics sunset
  window.dataLayer.push({
    event: 'productClick',
    ecommerce: {
      click: {
        actionField: {
          list, // What carousel
        },
        products: [
          {
            id,
            // Product sku
            position,
            // Location in the carousel
            name, // Artwork title
          },
        ],
      },
    },
  })
  // This is the new dataLayer event for GA4
  // Clear the previous ecommerce object.
  window.dataLayer.push({
    ecommerce: null,
  })
  window.dataLayer.push({
    event: 'select_item',
    ecommerce: {
      item_list_id: listid,
      item_list_name: listname,
      items: [
        {
          item_category: category.replace(/s$/, '').replace(/\sM/, 'm'),
          item_id: id || '',
          item_name: name || '',
          item_brand: brand || '',
          item_art_id: artid || '',
          item_variant: getDataLayerItemVariant(id),
          index: position + 1,
          item_list_id: listid,
          item_list_name: listname,
          affiliation: 'Online Store',
          price: convertFromCentsToDollars(price) || 0,
          quantity: 1,
        },
      ],
    },
  })
  // Algolia event
  if (algoliaSearchIndex && algoliaUserToken && userID) {
    algoliaEventsApi({
      events: [
        {
          eventName: 'select_item',
          eventType: 'click',
          index: algoliaSearchIndex,
          userToken: algoliaUserToken,
          authenticatedUserToken: String(userID) || '',
          objectIDs: [String(artid)],
          positions: [position],
          ...(algoliaQueryIdFromLocalStorage && {
            queryID: algoliaQueryIdFromLocalStorage,
          }), // pass queryID only if its present, otherwise do not pass
        },
      ],
    })
  }
}

export const addToCartGAWhitelist = ({
  artistName,
  category,
  price,
  sku,
  title,
  listId,
  dataPosition,
  artworkId,
}: AddToCartGAWhitelistParams): GA4CartEventObject => {
  // You can not add a PRODUCT with NO SKU so return empty object
  if (!sku) {
    return {
      event: '',
      ecommerce: null,
    }
  }

  const { productTypeId } = parseSku(sku) // [configurationId, userId, artworkId, productTypeId]

  return {
    event: `add_to_cart`,
    ecommerce: {
      currency: 'USD',
      value: convertFromCentsToDollarsString(price), // artwork price
      items: [
        {
          item_id: sku || '',
          item_name: title || '',
          affiliation: 'Online Store',
          index: dataPosition || 0,
          item_brand: artistName || '',
          item_art_id: `${artworkId}` || '',
          item_category: category || '',
          item_variant: PRODUCT_TYPES[productTypeId] || 'original',
          item_list_id: listId || '', // product list from where the product was added to cart
          price: convertFromCentsToDollarsString(price),
          quantity: 1,
        },
      ],
    },
  }
}

export const transformConstructorRecommendationsToProductCarousel = (
  recommendationsResponse: ReadonlyArray<
    ConstructorResultsSdkResponseObject<ConstructorResultsData>
  >
): ReadonlyArray<ArtworkObject> => {
  return recommendationsResponse.map(({ data, value }) => {
    const {
      id,
      artist_first_name,
      artist_last_name,
      artist_id,
      country,
      geo_prices,
      groups,
      image_url,
      materials,
      mediums,
      url,
      depth,
      height,
      width,
      artist_profile_url,
      print_min_price,
      original_availability_status,
      sku,
    } = data
    return {
      artworkId: Number(id),
      productUrl: url,
      profileUrl: artist_profile_url,
      baseImageUrl: image_url,
      title: value,
      artistId: Number(artist_id),
      artistName: `${artist_first_name} ${artist_last_name}`,
      sku,
      country,
      category: getCategoryFromGroups(groups),
      dimensions: { width, height, depth },
      originalStatus: original_availability_status,
      originalListPrices: geo_prices,
      minPrintPrice: print_min_price,
      materials,
      mediums,
      hasOpenEditions: Boolean(print_min_price),
    }
  })
}

type removeArtworkByIdFromRecommendationsParams = {
  recommendationsResponse: ReadonlyArray<
    ConstructorResultsSdkResponseObject<ConstructorResultsData>
  >
  artworkId: string
  maxNumberOfItems?: number
}

export const removeArtworkByIdFromRecommendations = ({
  recommendationsResponse,
  artworkId,
  maxNumberOfItems = 48,
}: removeArtworkByIdFromRecommendationsParams): ReadonlyArray<ConstructorResultsSdkResponseObject<
  ConstructorResultsData
>> => {
  const filteredRecommendations = recommendationsResponse.filter(({ data }) => {
    const { id } = data
    return `${id}` !== `${artworkId}`
  })

  if (filteredRecommendations.length > maxNumberOfItems) {
    return filteredRecommendations.slice(0, maxNumberOfItems)
  }

  return filteredRecommendations
}
